import React, { useState } from 'react';
import { Play, Heart, Share2, Clock, User, MoreHorizontal, Trash2, Bell, BellOff, X } from 'lucide-react';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { comboApi, alarmApi, urlBase64ToUint8Array } from '@/lib/api';
import { getComboColorHex } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';

const DAYS_OF_WEEK = [
  { key: 'lun', label: 'L' },
  { key: 'mar', label: 'M' },
  { key: 'mie', label: 'X' },
  { key: 'jue', label: 'J' },
  { key: 'vie', label: 'V' },
  { key: 'sab', label: 'S' },
  { key: 'dom', label: 'D' },
];

const ComboCard = ({ combo, onPlay, onUpdate, onDelete }) => {
  const { user } = useAuth();
  const [isSaved, setIsSaved] = useState(combo.is_saved);
  const [showMenu, setShowMenu] = useState(false);
  const [showAlarmModal, setShowAlarmModal] = useState(false);
  const [alarmTime, setAlarmTime] = useState(combo.alarm_time || '07:00');
  const [alarmDays, setAlarmDays] = useState(combo.alarm_days || []);
  const [isAlarmActive, setIsAlarmActive] = useState(combo.is_alarm);
  const [savingAlarm, setSavingAlarm] = useState(false);
  // Por defecto la alarma suena todos los días; el selector de días queda oculto
  const [showDaysPicker, setShowDaysPicker] = useState((combo.alarm_days || []).length > 0);

  const comboHex = getComboColorHex(combo);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const timeAgo = formatDistanceToNow(new Date(combo.created_at), {
    addSuffix: true,
    locale: es
  });

  const handleSave = async (e) => {
    e.stopPropagation();
    if (!user) return;
    try {
      const result = await comboApi.toggleSave(combo.combo_id);
      setIsSaved(result.is_saved);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Save error:', error);
    }
  };

  const handleShare = async (e) => {
    e.stopPropagation();
    const slug = combo.slug || combo.combo_id;
    const shareUrl = `${window.location.origin}/c/${slug}`;
    const audioCount = combo.audios?.length || 0;
    const duration = formatTime(combo.total_duration_seconds || 0);
    
    try {
      await comboApi.share(combo.combo_id);
      
      if (navigator.share) {
        await navigator.share({
          text: `Me he acordado de ti con esto 🤗😅 ${shareUrl}`
        });
      } else {
        await navigator.clipboard.writeText(`Me he acordado de ti con esto 🤗😅 ${shareUrl}`);
        alert('Enlace copiado');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!window.confirm('¿Eliminar este combo?')) return;
    try {
      await comboApi.delete(combo.combo_id);
      if (onDelete) onDelete(combo.combo_id);
    } catch (error) {
      console.error('Delete error:', error);
    }
  };

  const toggleDay = (day) => {
    setAlarmDays(prev => 
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const handleSaveAlarm = async () => {
    setSavingAlarm(true);
    try {
      await comboApi.update(combo.combo_id, {
        is_alarm: true,
        alarm_time: alarmTime,
        alarm_days: alarmDays
      });
      setIsAlarmActive(true);
      setShowAlarmModal(false);
      if (onUpdate) onUpdate();
      // Guardar en localStorage para el servicio de alarma
      const alarms = JSON.parse(localStorage.getItem('audioyou_alarms') || '{}');
      alarms[combo.combo_id] = { time: alarmTime, days: alarmDays, name: combo.name };
      localStorage.setItem('audioyou_alarms', JSON.stringify(alarms));
      // Guardar alarma por usuario en el servidor
      await alarmApi.setAlarm(combo.combo_id, alarmTime, alarmDays, combo.name);
      try {
        if ('serviceWorker' in navigator && 'PushManager' in window) {
          const permission = await Notification.requestPermission();
          if (permission === 'granted') {
            const vapidKey = await alarmApi.getVapidKey();
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.subscribe({
              userVisibleOnly: true,
              applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });
            await alarmApi.subscribePush(subscription.toJSON());
            // Push activo: quitar alarma local para no duplicar
            const alarms = JSON.parse(localStorage.getItem('audioyou_alarms') || '{}');
            delete alarms[combo.combo_id];
            localStorage.setItem('audioyou_alarms', JSON.stringify(alarms));
          }
        }
      } catch (pushErr) {
        console.log('Push no disponible, usando alarma local:', pushErr);
      }
    } catch (error) {
      console.error('Alarm save error:', error);
      alert('Error al guardar la alarma');
    } finally {
      setSavingAlarm(false);
    }
  };

  const handleRemoveAlarm = async () => {
    setSavingAlarm(true);
    try {
      await comboApi.update(combo.combo_id, {
        is_alarm: false,
        alarm_time: null,
        alarm_days: []
      });
      setIsAlarmActive(false);
      setShowAlarmModal(false);
      if (onUpdate) onUpdate();
      // Remover del localStorage
      const alarms = JSON.parse(localStorage.getItem('audioyou_alarms') || '{}');
      delete alarms[combo.combo_id];
      localStorage.setItem('audioyou_alarms', JSON.stringify(alarms));
      // Remover alarma del servidor
      try {
        await alarmApi.deleteAlarm(combo.combo_id);
      } catch (err) {
        console.log('Error al borrar alarma en servidor:', err);
      }
    } catch (error) {
      console.error('Alarm remove error:', error);
    } finally {
      setSavingAlarm(false);
    }
  };

  return (
    <div 
      className="neumorphic-card p-4 cursor-pointer hover:shadow-lg transition-shadow"
      onClick={() => onPlay(combo)}
      data-testid={`combo-card-${combo.combo_id}`}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
            style={comboHex ? { backgroundColor: comboHex } : { backgroundColor: '#A855F7' }}
          >
            <span className="text-xl">{combo.emoji || '🎵'}</span>
          </div>
          <div>
            <h3 className="font-semibold text-gray-800 text-lg">{combo.name}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Clock size={14} />
              <span>{formatTime(combo.total_duration_seconds)}</span>
              <span>•</span>
              <span>{combo.audios.length} audios</span>
            </div>
          </div>
        </div>

        {/* Menu for owner */}
        {combo.is_owner && (
          <div className="relative">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(!showMenu);
              }}
              className="neumorphic-button-small p-2"
              data-testid="combo-menu-btn"
            >
              <MoreHorizontal size={16} />
            </button>
            {showMenu && (
              <div className="absolute right-0 top-10 neumorphic-card p-2 z-10 min-w-[120px]">
                <button
                  onClick={handleDelete}
                  className="flex items-center gap-2 px-3 py-2 text-red-500 hover:bg-red-50 rounded w-full"
                  data-testid="delete-combo-btn"
                >
                  <Trash2 size={16} />
                  <span>Eliminar</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Audio preview (first 5) */}
      <div className="flex items-center gap-2 mb-3 overflow-x-auto pb-2">
        {combo.audios.slice(0, 5).map((audio, index) => (
          <div 
            key={audio.audio_id}
            className="flex-shrink-0 flex items-center gap-1 px-2 py-1 bg-gray-50 rounded-lg"
          >
            <span className="text-xs text-gray-400">{index + 1}</span>
            <AudioVisualBadge
              shape={audio.shape}
              color={audio.color}
              emoji={audio.emoji}
              language={audio.language}
              size="sm"
            />
          </div>
        ))}
        {combo.audios.length > 5 && (
          <span className="text-xs text-gray-400 flex-shrink-0">
            +{combo.audios.length - 5} más
          </span>
        )}
      </div>

      {/* Author & stats */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          {combo.user_picture ? (
            <img 
              src={combo.user_picture} 
              alt={combo.username}
              className="w-6 h-6 rounded-full"
            />
          ) : (
            <User size={16} className="text-gray-400" />
          )}
          <span className="text-sm text-gray-600">@{combo.username}</span>
          <span className="text-xs text-gray-400">{timeAgo}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Alarm button - only for owner or saved */}
          {user && (combo.is_owner || isSaved) && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAlarmModal(true);
              }}
              className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
              data-testid="alarm-combo-btn"
              title="Configurar alarma"
            >
              {isAlarmActive ? (
                <Bell size={18} className="text-amber-500 fill-amber-500" />
              ) : (
                <BellOff size={18} className="text-gray-400" />
              )}
            </button>
          )}
          <button
            onClick={handleSave}
            disabled={!user}
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            data-testid="save-combo-card"
          >
            <Heart 
              size={18} 
              className={isSaved ? 'fill-red-500 text-red-500' : 'text-gray-400'}
            />
          </button>
          <button
            onClick={handleShare}
            className="p-2 hover:bg-gray-50 rounded-lg transition-colors"
            data-testid="share-combo-card"
          >
            <Share2 size={18} className="text-gray-400" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onPlay(combo);
            }}
            className="neumorphic-button-accent px-3 py-2 flex items-center gap-2"
            data-testid="play-combo-btn"
          >
            <Play size={16} />
            <span className="text-sm">Play</span>
          </button>
        </div>
      </div>

      {/* Alarm indicator */}
      {isAlarmActive && alarmTime && (
        <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2 text-sm text-audioyou-accent">
          <span>⏰</span>
          <span>Alarma: {alarmTime}</span>
          {alarmDays.length > 0 && (
            <span className="text-xs text-gray-400">
              ({alarmDays.join(', ')})
            </span>
          )}
        </div>
      )}

      {/* Alarm Modal */}
      {showAlarmModal && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">⏰ Configurar Alarma</h3>
              <button 
                onClick={() => setShowAlarmModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500 mb-4">
              Combo: <strong>{combo.name}</strong>
            </p>

            {/* Time picker */}
            <div className="mb-4">
              <label className="block text-sm text-gray-600 mb-2">Hora</label>
              <input
                type="time"
                value={alarmTime}
                onChange={(e) => setAlarmTime(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-lg font-mono focus:outline-none focus:ring-2 focus:ring-audioyou-accent"
                data-testid="alarm-time-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Sonará a las {new Date(2000, 0, 1, ...alarmTime.split(':').map(Number)).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} {alarmDays.length === 0 ? 'todos los días' : ''}
              </p>
            </div>

            {/* Days picker (oculto tras toggle: por defecto suena todos los días) */}
            <div className="mb-6">
              <button
                onClick={() => setShowDaysPicker(!showDaysPicker)}
                className="text-sm text-audioyou-accent font-medium hover:underline"
                data-testid="toggle-days-picker"
              >
                {showDaysPicker ? '▾ Personalizar días' : '▸ Personalizar días'}
              </button>
              {showDaysPicker ? (
                <>
                  <div className="flex gap-2 justify-center mt-2">
                    {DAYS_OF_WEEK.map(day => (
                      <button
                        key={day.key}
                        onClick={() => toggleDay(day.key)}
                        className={`w-10 h-10 rounded-full font-medium text-sm transition-all ${
                          alarmDays.includes(day.key)
                            ? 'bg-audioyou-accent text-white'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        data-testid={`alarm-day-${day.key}`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-gray-400 text-center mt-2">
                    {alarmDays.length === 0 ? 'Todos los días' : `Cada ${alarmDays.join(', ')}`}
                  </p>
                </>
              ) : (
                <p className="text-xs text-gray-400 text-center mt-1">
                  Por defecto: todos los días
                </p>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-3">
              {isAlarmActive && (
                <button
                  onClick={handleRemoveAlarm}
                  disabled={savingAlarm}
                  className="flex-1 py-3 px-4 border border-red-300 text-red-500 rounded-xl font-medium hover:bg-red-50"
                  data-testid="remove-alarm-btn"
                >
                  Quitar alarma
                </button>
              )}
              <button
                onClick={handleSaveAlarm}
                disabled={savingAlarm}
                className="flex-1 py-3 px-4 bg-audioyou-accent text-white rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
                data-testid="save-alarm-btn"
              >
                {savingAlarm ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComboCard;
